import { defined } from "@glideapps/ts-necessities";
import { getLocalizedString } from "../../components/localization";
import { asMaybeString } from "../../computation-model/data";
import { SerializedComponent } from "../../components/SerializedApp";
import { getFeatureSetting } from "../../feature-settings";
import { isEmptyOrUndefined, panic } from "../../Support";
import { makeText } from "../builder-utils";
import {
    ComponentBuilder,
    ComponentConfiguratorContext,
    WireActionHydrator,
    WireInflationBackend,
} from "../components";
import { makeTextPropertyDescriptor } from "../components/descriptor-utils";
import { EnumPropertyHandler } from "../components/property-handlers";
import { PropertySection } from "../components/property-sections";
import { ActionKind, InputOutputTables, MutatingScreenKind } from "../description";
import { ShowToastActionDescription, ToastIcon } from "../form-on-submit";
import { ActionDescriptor } from "./action-descriptor";
import { DescriptionToken } from "./action-handler";
import { BaseActionHandler, tokenForProperty } from "./base";

const iconPropertyHandler = new EnumPropertyHandler(
    { icon: ToastIcon.Success },
    "Icon",
    "Icon",
    [
        {
            label: "Success",
            value: ToastIcon.Success,
        },
        {
            label: "Failure",
            value: ToastIcon.Failure,
        },
    ],
    PropertySection.Data,
    "dropdown"
);

function isSuccess(desc: ShowToastActionDescription): boolean {
    return iconPropertyHandler.getEnum(desc) === ToastIcon.Success;
}

export class ShowToastHandler extends BaseActionHandler<ShowToastActionDescription> {
    public readonly kind = ActionKind.ShowToast;
    // FIXME: proper icon
    public readonly iconName = "clipboard";

    public getDescriptor(
        _desc: ShowToastActionDescription | undefined,
        _tables: InputOutputTables | undefined,
        _ccc: ComponentConfiguratorContext,
        mutatingScreenKind: MutatingScreenKind | undefined
    ): ActionDescriptor {
        return {
            name: "Show Notification",
            isLegacy: !getFeatureSetting("showToastAction"),
            needsScreenContext: true,
            properties: [
                makeTextPropertyDescriptor("message", "Message", "Success", true, mutatingScreenKind, {
                    preferredNames: ["message", "toast"],
                    searchable: false,
                    emptyByDefault: true,
                }),
                iconPropertyHandler,
            ],
        };
    }

    public makeAction(
        builder: ComponentBuilder,
        desc: ShowToastActionDescription,
        tables: InputOutputTables,
        screenContext: SerializedComponent | undefined
    ): SerializedComponent | undefined {
        const { b } = builder;
        if (screenContext === undefined) {
            return panic("Must have screen context for show toast action");
        }

        const success = isSuccess(desc);

        let message = makeText(builder, desc.message, tables.input, true, true, false, false, screenContext);
        if (message === undefined) {
            message = b.localizedString(success ? "formSubmissionSuccess" : "error");
        }

        return b.showToast(message, success);
    }

    public getTokenizedDescription(
        desc: ShowToastActionDescription,
        tables: InputOutputTables,
        ccc: ComponentConfiguratorContext
    ): readonly DescriptionToken[] | undefined {
        const columnToken = tokenForProperty(desc.message, tables, ccc);
        if (columnToken === undefined) return undefined;

        return [
            {
                kind: "string",
                value: isSuccess(desc) ? "🎉 " : "❗️ ",
            },
            columnToken,
        ];
    }

    public inflate(ib: WireInflationBackend, desc: ShowToastActionDescription): WireActionHydrator | undefined {
        const success = isSuccess(desc);
        const [messageGetter] = ib.getValueGetterForProperty(desc.message, true);
        return vp => {
            let message = asMaybeString(messageGetter(vp));
            if (isEmptyOrUndefined(message)) {
                message = getLocalizedString(success ? "formSubmissionSuccess" : "error");
            }

            return async ab => {
                ab.actionCallbacks.showToast(success, defined(message));
                return true;
            };
        };
    }
}
